import { isIsoDuration, parseISO8601Duration } from '@gfe/core/utils/time-utils';

import { FindingSize, FindingSizeWord, NormalizedWarning } from './warning.model';

function seconds(raw: unknown): number | undefined {
  if (typeof raw !== 'string' || !isIsoDuration(raw)) return undefined;
  return parseISO8601Duration(raw).totalMilliseconds / 1000;
}

/** A finding off its rule by nothing has no size to state: "worst 0 s over" reads as noise. */
function sized(value: number | undefined, word: FindingSizeWord): FindingSize | undefined {
  return value !== undefined && value > 0 ? { seconds: value, word } : undefined;
}

function windowViolation(entry: NormalizedWarning): FindingSize | undefined {
  const direction = entry.payload['early_or_late'];
  if (typeof direction !== 'string') return undefined;
  const word = direction.toLowerCase();
  if (word !== 'late' && word !== 'early') return undefined;
  return sized(seconds(entry.payload['violation']), word);
}

function waitOverMaximum(entry: NormalizedWarning): FindingSize | undefined {
  const accumulated = seconds(entry.payload['accumulated_dur']);
  const maximum = seconds(entry.payload['maximum_dur']);
  if (accumulated === undefined || maximum === undefined) return undefined;
  return sized(accumulated - maximum, 'over');
}

/**
 * How far each code's finding is off its rule, read from one payload duration. A code
 * absent from this table has no size, and its findings keep their order behind sized ones.
 */
const SIZES: Record<string, (entry: NormalizedWarning) => FindingSize | undefined> = {
  windows_respected_check: windowViolation,
  soft_windows_respected_check: windowViolation,
  work_regulation_pause_check: (entry) =>
    sized(seconds(entry.payload['excess_dur']), 'over'),
  consecutive_wait_duration_check: waitOverMaximum,
  max_total_wait_duration_respected_check: waitOverMaximum
};

export function findingSize(entry: NormalizedWarning): FindingSize | undefined {
  return SIZES[entry.code]?.(entry);
}
